import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import GemDivider from "@/components/GemDivider";
import CompetitorCard from "@/components/CompetitorCard";
import { PROMPTBOUND, COMPETITORS, SPARSE_FOOTPRINT_NOTE } from "@/lib/competitorFacts";
import { btnPrimary, btnSecondary, card, label } from "@/lib/styles";

const byId = (id) => COMPETITORS.find((c) => c.id === id);

// Alphabetical, so the ordering can't read as us stacking the deck for
// Promptbound (which lands last alphabetically among these seven names).
const ORDER = ["educative", "exponent", "final-round-ai", "interview-kickstart", "leetcode"];

export default function BestAiEngineerInterviewPrepPlatforms() {
  usePageMeta(
    "Best AI Engineer Interview Prep Platforms (2026)",
    "A 2026 roundup of interview prep platforms for AI engineer, MLE and GenAI roles: Promptbound, Interview Kickstart, Final Round AI, Educative, Exponent, LeetCode and more, compared on price and role fit."
  );

  const platforms = ORDER.map(byId);

  return (
    <div className="min-h-screen grid-bg">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className={label}>Comparison guide</span>
        <h1 className="mt-3 font-serif font-bold text-3xl sm:text-4xl text-zinc-100">
          Best AI engineer interview prep platforms (2026)
        </h1>
        <p className="mt-5 text-zinc-400 leading-relaxed">
          Search this phrase and most results are static lists of sample questions, not a comparison of the
          platforms themselves for the AI engineer, MLE, GenAI or Applied Scientist track specifically. This page is
          that comparison: six platforms, alphabetical order, compared on price and reviews where we could verify
          them, and on fit for AI-specific roles.
        </p>
        <div className={`${card} mt-6 p-5 flex gap-3 items-start`}>
          <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-400 leading-relaxed">
            Full disclosure: this page is published by Promptbound. Every claim below, about every product including
            ours, is sourced and dated. Where we don't have a verified number, we say so instead of guessing.
          </p>
        </div>
      </section>

      <GemDivider />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif font-bold text-2xl text-zinc-100 mb-6">The platforms, compared</h2>
        <div className="space-y-4">
          {platforms.map((c) => <CompetitorCard key={c.id} competitor={c} />)}
          <CompetitorCard competitor={PROMPTBOUND} isPromptbound />
        </div>
        <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
          Interview Query, interviewing.io, Hello Interview and NeetCode also come up in this space. {SPARSE_FOOTPRINT_NOTE}
        </p>
      </section>

      <GemDivider />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="font-serif font-bold text-2xl text-zinc-100 mb-4">Which platform actually fits an AI-engineer loop</h2>
        <ul className="space-y-3 text-sm text-zinc-400 leading-relaxed list-disc list-inside">
          <li><span className="text-zinc-200 font-semibold">Real-loop questions for AI/ML roles, free lessons first:</span> Promptbound. 72 of Altaris's roughly 177 graded-interview questions are adapted from real 2024-2026 Meta, Google, Amazon, OpenAI and Anthropic loops, and all 40 lessons are free forever.</li>
          <li><span className="text-zinc-200 font-semibold">A live, instructor-led bootcamp with a dedicated ML track:</span> Interview Kickstart's ML Flagship program, if a five-figure budget works for you.</li>
          <li><span className="text-zinc-200 font-semibold">AI-driven mock interview reps at a lower monthly price than a bootcamp:</span> Final Round AI, factor in the Trustpilot-versus-Product-Hunt rating gap above first.</li>
          <li><span className="text-zinc-200 font-semibold">Broader CS fundamentals alongside interview prep:</span> Educative.</li>
          <li><span className="text-zinc-200 font-semibold">General tech or PM interview prep:</span> Exponent, with the caveat that reviewers have flagged it as weaker specifically for AI roles.</li>
          <li><span className="text-zinc-200 font-semibold">Raw algorithm and data-structure reps:</span> LeetCode, useful alongside any of the above since most AI-engineer loops still include a coding round.</li>
          <li><span className="text-zinc-200 font-semibold">Interview Query, interviewing.io, Hello Interview or NeetCode:</span> check their sites directly, we don't have enough verified data on any of them yet to weigh in confidently.</li>
        </ul>

        <div className="mt-14 text-center border border-gold/25 rounded-xl bg-gradient-to-b from-[#141d34]/60 to-transparent p-10 gold-glow">
          <h3 className="font-serif font-bold text-2xl text-zinc-100">Start with the free curriculum</h3>
          <p className="text-sm text-zinc-400 mt-2 mb-7 max-w-md mx-auto">
            All 40 lessons, no card required. 3 graded interviews with Altaris are free too, then it's $20/mo or
            $192/yr for unlimited.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" data-testid="aiprep-cta-register" className={btnPrimary}>
              Create free account <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" data-testid="aiprep-cta-pricing" className={btnSecondary}>
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
